// Email and SMS delivery preferences. Requirement 6.
//
// Loads the current notification settings, lets the operator edit the email destination
// and the Telegram chat ID, toggle each channel, and send a test notification.

import { useEffect, useState } from "react";

import { Share2 } from "lucide-react";

import { useApi } from "../api/useApi";
import { useInviteShare } from "../lib/useInviteShare";
import type { NotificationSettings } from "../types";
import { DeleteAccount } from "./DeleteAccount";
import { TwoFactorSettings } from "./TwoFactorSettings";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// The deployment's Telegram bot handle (without the leading @), injected at build time so
// the setup steps can link straight to the bot. When unset, the steps fall back to a
// generic "search for the bot" wording.
const telegramBotHandle = (import.meta.env.VITE_TELEGRAM_BOT as string | undefined)?.replace(
  /^@/,
  "",
);

export function NotificationSettingsPage() {
  const api = useApi();
  const invite = useInviteShare();
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [status, setStatus] = useState("");

  useEffect(() => {
    void api
      .getNotificationSettings()
      .then(setSettings)
      .catch((err: unknown) =>
        setStatus(err instanceof Error ? err.message : "Failed to load settings"),
      );
  }, [api]);

  if (!settings) {
    return (
      <section className="space-y-2">
        <p className="text-muted-foreground">{status || "Loading…"}</p>
      </section>
    );
  }

  const patch = (next: Partial<NotificationSettings>) =>
    setSettings((prev) => (prev ? { ...prev, ...next } : prev));

  const save = async () => {
    setStatus("");
    try {
      const saved = await api.updateNotificationSettings({
        email: settings.email,
        email_enabled: settings.email_enabled,
        telegram_chat_id: settings.telegram_chat_id,
        telegram_enabled: settings.telegram_enabled,
      });
      setSettings(saved);
      setStatus("Settings saved.");
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Save failed");
    }
  };

  const sendTest = async () => {
    setStatus("");
    try {
      const resp = await api.testNotifications();
      setStatus(resp.sent ? "Test notification sent." : "Test notification not sent.");
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Test failed");
    }
  };

  const connectTelegram = async () => {
    setStatus("");
    try {
      // Mint a single-use deep-link token and open the bot. Pressing Start there sends
      // /start <token> to the webhook, which binds this chat to the account automatically.
      const { url } = await api.createTelegramLinkToken();
      window.open(url, "_blank", "noopener,noreferrer");
      setStatus(
        "Opening Telegram. Press Start in the bot to connect, then reload this page to see it enabled.",
      );
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not start Telegram connect");
    }
  };

  const reloadSettings = async () => {
    setStatus("");
    try {
      setSettings(await api.getNotificationSettings());
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Failed to reload settings");
    }
  };

  const disconnectTelegram = async () => {
    setStatus("");
    try {
      // Clear the linked chat and disable the channel, keeping email settings intact.
      const saved = await api.updateNotificationSettings({
        email: settings.email,
        email_enabled: settings.email_enabled,
        telegram_chat_id: "",
        telegram_enabled: false,
      });
      setSettings(saved);
      setStatus("Telegram disconnected.");
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not disconnect Telegram");
    }
  };

  return (
    <section className="max-w-2xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Email</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Address</Label>
            <Input
              id="email"
              type="email"
              value={settings.email ?? ""}
              onChange={(e) => patch({ email: e.target.value })}
              aria-label="email"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-primary"
              checked={settings.email_enabled}
              onChange={(e) => patch({ email_enabled: e.target.checked })}
              aria-label="email-enabled"
            />
            Send email alerts
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Telegram</CardTitle>
          <CardDescription>
            Free push alerts with one-minute setup.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.telegram_enabled && settings.telegram_chat_id ? (
            // Already connected: show status and let the user disconnect or reconnect,
            // rather than presenting the first-time "Connect" call to action again.
            <div className="space-y-2">
              <p className="text-sm">
                <span className="font-medium text-success">Connected</span>{" "}
                <span className="text-muted-foreground">
                  (chat ID {settings.telegram_chat_id}). Alerts will be sent here.
                </span>
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" onClick={() => void disconnectTelegram()}>
                  Disconnect
                </Button>
                <button
                  type="button"
                  className="text-xs font-medium text-primary hover:underline"
                  onClick={() => void connectTelegram()}
                >
                  Reconnect a different chat
                </button>
              </div>
            </div>
          ) : (
            // Not connected: the one-tap flow. Mints a deep-link token, opens the bot, and
            // binds this chat to the account automatically when the user presses Start.
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Not connected yet.</p>
              <Button type="button" onClick={() => void connectTelegram()}>
                Connect Telegram
              </Button>
              <p className="text-xs text-muted-foreground">
                Opens the smart-margin bot. Press <span className="font-medium">Start</span>{" "}
                and you're linked, no copy-paste. Then{" "}
                <button
                  type="button"
                  className="font-medium text-primary hover:underline"
                  onClick={() => void reloadSettings()}
                >
                  reload
                </button>{" "}
                to confirm.
              </p>
            </div>
          )}

          {/* Fallback: manual chat-ID entry, kept for when the deep link cannot be used
              (e.g. desktop without the Telegram app handling t.me links). */}
          <details className="rounded-md border border-border/60 p-3">
            <summary className="cursor-pointer text-sm text-muted-foreground">
              Enter a chat ID manually instead
            </summary>
            <div className="mt-3 space-y-3">
              <p className="text-xs text-muted-foreground">
                {telegramBotHandle ? (
                  <>
                    Message{" "}
                    <a
                      href={`https://t.me/${telegramBotHandle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-primary hover:underline"
                    >
                      @{telegramBotHandle}
                    </a>{" "}
                  </>
                ) : (
                  <>Message the smart-margin bot </>
                )}
                any text and it replies with your numeric chat ID. Paste it here, tick the
                box, and save.
              </p>
              <div className="space-y-2">
                <Label htmlFor="telegram-chat-id">Telegram chat ID</Label>
                <Input
                  id="telegram-chat-id"
                  inputMode="numeric"
                  placeholder="e.g. 123456789"
                  value={settings.telegram_chat_id ?? ""}
                  onChange={(e) => patch({ telegram_chat_id: e.target.value })}
                  aria-label="telegram-chat-id"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="size-4 accent-primary"
                  checked={settings.telegram_enabled}
                  onChange={(e) => patch({ telegram_enabled: e.target.checked })}
                  aria-label="telegram-enabled"
                />
                Send Telegram alerts
              </label>
            </div>
          </details>
          <p className="text-xs text-muted-foreground">
            After saving, use <span className="font-medium">Send test notification</span>{" "}
            below to confirm the bot can reach you.
          </p>
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button type="button" onClick={() => void save()}>
          Save settings
        </Button>
        <Button type="button" variant="outline" onClick={() => void sendTest()}>
          Send test notification
        </Button>
      </div>

      {status && <p className="status text-sm text-muted-foreground">{status}</p>}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Invite a friend</CardTitle>
          <CardDescription>
            Share the signup link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            onClick={() => void invite.share()}
            data-testid="settings-invite"
          >
            <Share2 className="size-4" />
            {invite.label}
          </Button>
        </CardContent>
      </Card>

      <TwoFactorSettings />

      <DeleteAccount />
    </section>
  );
}
